import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Star, Save, RotateCcw } from 'lucide-react';
import ImageUploadField from '../../common/ImageUploadField';

const FAQ_CATEGORIES = [
  { value: 'general', label: 'General Questions', icon: '❓' },
  { value: 'booking', label: 'Booking & Services', icon: '📅' },
  { value: 'payment', label: 'Payments & Billing', icon: '💳' },
  { value: 'provider', label: 'For Service Providers', icon: '🔧' }
];

export function TestimonialDialog({ 
  open, 
  onClose, 
  editItem,
  form, 
  setForm, 
  onSave, 
  isSaving 
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-[Poppins]">{editItem ? 'Edit Review' : 'Add Customer Review'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Customer Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} 
                className="h-11 mt-1.5"
                placeholder="Maria Santos"
              />
            </div>
            <div>
              <Label className="text-slate-700">Location</Label>
              <Input 
                value={form.location} 
                onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} 
                className="h-11 mt-1.5"
                placeholder="Makati City"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Rating</Label>
            <div className="flex gap-2 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={`rating-${star}`}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, rating: star }))}
                  className="p-1"
                >
                  <Star className={`w-8 h-8 transition-colors ${star <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Testimonial Text *</Label>
            <Textarea 
              value={form.text} 
              onChange={(e) => setForm(p => ({ ...p, text: e.target.value }))} 
              className="mt-1.5" 
              rows={4}
              placeholder="Share the customer's experience..."
            />
          </div>
          <div>
            <Label className="text-slate-700">Photo (optional)</Label>
            <div className="mt-1.5">
              <ImageUploadField
                value={form.avatar_url}
                onChange={(val) => setForm(p => ({ ...p, avatar_url: val }))}
                placeholder="Upload customer photo"
                testId="testimonial-avatar"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
            <div>
              <Label className="text-slate-700">Show on Website</Label>
              <p className="text-xs text-slate-500">Toggle off to hide without deleting</p>
            </div>
          </div>
          <Button className="w-full h-11 bg-slate-900 hover:bg-slate-800" onClick={onSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Review'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FAQDialog({ 
  open, 
  onClose, 
  editItem,
  form, 
  setForm, 
  onSave, 
  isSaving 
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-[Poppins]">{editItem ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-slate-700">Question *</Label>
            <Input 
              value={form.question} 
              onChange={(e) => setForm(p => ({ ...p, question: e.target.value }))} 
              className="h-11 mt-1.5"
              placeholder="How do I book a service?"
            />
          </div>
          <div>
            <Label className="text-slate-700">Answer *</Label>
            <Textarea 
              value={form.answer} 
              onChange={(e) => setForm(p => ({ ...p, answer: e.target.value }))} 
              className="mt-1.5" 
              rows={4}
              placeholder="Provide a helpful answer..."
            />
          </div>
          <div>
            <Label className="text-slate-700">Category</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {FAQ_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, category: cat.value }))}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.category === cat.value 
                      ? 'border-amber-500 bg-amber-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <span className="text-lg mr-2">{cat.icon}</span>
                  <span className="text-sm font-medium text-slate-700">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
            <div>
              <Label className="text-slate-700">Show on Website</Label>
              <p className="text-xs text-slate-500">Toggle off to hide without deleting</p>
            </div>
          </div>
          <Button className="w-full h-11 bg-slate-900 hover:bg-slate-800" onClick={onSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save FAQ'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RestoreDefaultsDialog({
  open,
  onClose,
  options,
  setOptions,
  onRestore,
  isRestoring
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Restore Default Content</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-slate-600">
            Select what you want to restore. This will add default items that don&apos;t already exist.
          </p>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
              <div>
                <Label className="text-slate-700">FAQs</Label>
                <p className="text-xs text-slate-500">15 default FAQ items</p>
              </div>
              <Switch 
                checked={options.restore_faqs} 
                onCheckedChange={(v) => setOptions(p => ({ ...p, restore_faqs: v }))} 
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
              <div>
                <Label className="text-slate-700">Testimonials</Label>
                <p className="text-xs text-slate-500">8 sample reviews</p>
              </div>
              <Switch 
                checked={options.restore_testimonials} 
                onCheckedChange={(v) => setOptions(p => ({ ...p, restore_testimonials: v }))} 
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
              <div>
                <Label className="text-slate-700">Site Identity</Label>
                <p className="text-xs text-slate-500">Reset title, tagline (clears logo)</p>
              </div>
              <Switch 
                checked={options.restore_site_identity} 
                onCheckedChange={(v) => setOptions(p => ({ ...p, restore_site_identity: v }))} 
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
              <div>
                <Label className="text-slate-700">Landing Page</Label>
                <p className="text-xs text-slate-500">Hero, features, footer sections</p>
              </div>
              <Switch 
                checked={options.restore_landing} 
                onCheckedChange={(v) => setOptions(p => ({ ...p, restore_landing: v }))} 
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-200">
              <div>
                <Label className="text-red-700">Clear Existing First</Label>
                <p className="text-xs text-red-500">⚠️ Deletes current data before restoring</p>
              </div>
              <Switch 
                checked={options.clear_existing} 
                onCheckedChange={(v) => setOptions(p => ({ ...p, clear_existing: v }))} 
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={onRestore}
              disabled={isRestoring}
              data-testid="confirm-restore-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {isRestoring ? 'Restoring...' : 'Restore'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
